// src/client/utils/loanUtils.ts
// CLIENT-SIDE preview only. The authoritative interest/cost computation
// runs in Edge Functions (server-side) — this is a presentational mirror
// for the loan-apply flow's "all-in cost" disclosure.
import { kwachaToNgwee, type Ngwee } from '@/lib/types';
import type { LoanTermDays } from '@/constants/config';

export const INTEREST_RATES = {
  14: 0.10,
  30: 0.15,
  60: 0.22,
  90: 0.28,
} as const satisfies Record<LoanTermDays, number>;

export const PROCESSING_FEE_NGWEE: Ngwee = kwachaToNgwee(25);

export function computeInterest(principal: Ngwee, term: LoanTermDays): Ngwee {
  const rate = INTEREST_RATES[term];
  return Math.round(Number(principal) * rate) as Ngwee;
}

export interface AllInCost {
  principal: Ngwee;
  interest: Ngwee;
  processingFee: Ngwee;
  totalDue: Ngwee;
  /** Effective Annual Rate per BoZ Cost of Borrowing Regulations 1995.
   *  Formula: (interest + fee) / principal × (365 / term_days).
   *  Stored as a decimal — e.g. 1.977 means 198% p.a. Display via formatEAR(). */
  ear: number;
}

export function computeAllInCost(principal: Ngwee, term: LoanTermDays): AllInCost {
  const interest = computeInterest(principal, term);
  const totalDue = (Number(principal) + Number(interest) + Number(PROCESSING_FEE_NGWEE)) as Ngwee;
  const totalFinanceCost = Number(interest) + Number(PROCESSING_FEE_NGWEE);
  const ear = totalFinanceCost / Number(principal) * (365 / term);
  return { principal, interest, processingFee: PROCESSING_FEE_NGWEE, totalDue, ear };
}

/** Display the EAR as an integer percentage with "p.a." suffix per BoZ convention.
 *  e.g. formatEAR(1.977) → "198% p.a." */
export function formatEAR(ear: number): string {
  return `${Math.round(ear * 100)}% p.a.`;
}

export interface ScheduleEntry {
  dueAt: string;     // ISO
  amount: Ngwee;
}

const MS_PER_DAY = 86_400_000;

function addDays(iso: string, days: number): string {
  return new Date(new Date(iso).getTime() + days * MS_PER_DAY).toISOString();
}

export function computeRepaymentSchedule(
  principal: Ngwee,
  term: LoanTermDays,
  disbursedAtIso: string,
): ScheduleEntry[] {
  const { totalDue } = computeAllInCost(principal, term);
  // Per business rules in CLAUDE.md.
  if (term === 14) {
    return [{ dueAt: addDays(disbursedAtIso, 14), amount: totalDue }];
  }
  if (term === 30) {
    const half = Math.floor(Number(totalDue) / 2) as Ngwee;
    const remainder = (Number(totalDue) - Number(half)) as Ngwee;
    return [
      { dueAt: addDays(disbursedAtIso, 15), amount: half },
      { dueAt: addDays(disbursedAtIso, 30), amount: remainder },
    ];
  }
  if (term === 60) {
    const half = Math.floor(Number(totalDue) / 2) as Ngwee;
    const remainder = (Number(totalDue) - Number(half)) as Ngwee;
    return [
      { dueAt: addDays(disbursedAtIso, 30), amount: half },
      { dueAt: addDays(disbursedAtIso, 60), amount: remainder },
    ];
  }
  // 90-day: three even-ish installments
  const third = Math.floor(Number(totalDue) / 3) as Ngwee;
  const remainder = (Number(totalDue) - 2 * Number(third)) as Ngwee;
  return [
    { dueAt: addDays(disbursedAtIso, 30), amount: third },
    { dueAt: addDays(disbursedAtIso, 60), amount: third },
    { dueAt: addDays(disbursedAtIso, 90), amount: remainder },
  ];
}
